import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { fetch, pessimisticUpdate } from '@ngrx/router-store/data-persistence';
import { from } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { EduContentQueries } from '.';
import { EDU_CONTENT_SERVICE_TOKEN, EduContentServiceInterface } from '../../edu-content/edu-content.service.interface';
import { DalState } from '../dal.state.interface';
import { RecommendationActions } from '../recommendation';
import { EduContentDataFieldActionFactory } from './edu-content-data-field-action.factory';
import {
  AddEduContentsForBook,
  AddGeneralFilesForBook,
  EduContentsActionTypes,
  EduContentsLoadError,
  LoadData,
  LoadDataFail,
  LoadDataSuccess,
  LoadEduContentsForBook,
  LoadGeneralFilesForBook,
  NavigateToEduContentDetail,
  StartAddEduContent,
  UpsertEduContents,
} from './edu-content.actions';

@Injectable()
export class EduContentsEffects {
  private actions: Actions = inject(Actions);
  private store: Store<DalState> = inject(Store);
  private eduContentService: EduContentServiceInterface = inject(EDU_CONTENT_SERVICE_TOKEN);
  private router: Router = inject(Router);

  loadEduContentsForBook$ = createEffect(() =>
    this.actions.pipe(
      ofType(EduContentsActionTypes.LoadEduContentsForBook),
      concatLatestFrom(() => this.store.select(EduContentQueries.selectEduContentState)),
      fetch({
        run: (action: LoadEduContentsForBook, eduContentState) => {
          const { bookId } = action.payload;

          if (!action.payload.force && eduContentState.loadedForBook[bookId]) return;

          return this.eduContentService
            .getAllForBook(bookId)
            .pipe(map((eduContents) => new AddEduContentsForBook({ bookId, eduContents })));
        },
        onError: (action: LoadEduContentsForBook, error) => {
          return new EduContentsLoadError(error);
        },
      })
    )
  );

  loadGeneralFilesForBook$ = createEffect(() =>
    this.actions.pipe(
      ofType(EduContentsActionTypes.LoadGeneralFilesForBook),
      concatLatestFrom(() => this.store.select(EduContentQueries.selectEduContentState)),
      fetch({
        run: (action: LoadGeneralFilesForBook, eduContentState) => {
          const { bookId } = action.payload;
          if (!action.payload.force && eduContentState.loadedGeneralFilesForBook[bookId]) return;

          return this.eduContentService
            .getGeneralEduContentForBookId(bookId)
            .pipe(map((eduContents) => new AddGeneralFilesForBook({ bookId, eduContents })));
        },
        onError: (action: LoadGeneralFilesForBook, error) => {
          return new EduContentsLoadError(error);
        },
      })
    )
  );

  createEduContent$ = createEffect(() =>
    this.actions.pipe(
      ofType(EduContentsActionTypes.StartAddEduContent),
      pessimisticUpdate({
        run: (action: StartAddEduContent) => {
          return this.eduContentService
            .createEduContentDraft(
              action.payload.personPreferenceId,
              action.payload.eduContentBookId,
              action.payload.eduContentId
            )
            .pipe(
              switchMap((eduContentId) => {
                const actions: Action[] = [];
                // FYI: no eduContent is added to the store
                // Not needed at the moment
                if (action.payload.navigateAfterCreate) {
                  actions.push(new NavigateToEduContentDetail({ eduContentId }));
                }
                return from(actions);
              })
            );
        },
        onError: (action: StartAddEduContent, error) => {
          return new EduContentsLoadError(error);
        },
      })
    )
  );

  redirectToEduContentDetail$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(EduContentsActionTypes.NavigateToEduContentDetail),
        tap((action: NavigateToEduContentDetail) => {
          this.router.navigate(['/edu-contents-v2', action.payload.eduContentId]);
        })
      ),
    { dispatch: false }
  );

  upsertEduContentsFromRecommendation$ = createEffect(() =>
    this.actions.pipe(
      ofType(RecommendationActions.recommendationsForContextLoaded),
      map((action) => {
        const recommendations = action.recommendations || [];
        const eduContents = recommendations.map((rec) => rec.content).filter(Boolean);

        return new UpsertEduContents({ eduContents });
      })
    )
  );

  loadData$ = createEffect(() =>
    this.actions.pipe(
      ofType(EduContentsActionTypes.LoadData),
      fetch({
        run: (action: LoadData) => {
          const { eduContentId, options } = action.payload;
          // default to empty array if undefined
          const fields = action.payload.fields || [];

          return this.eduContentService.getData(eduContentId, fields, options).pipe(
            switchMap((data) => {
              const actions: Action[] = [];
              if (data) {
                // Generate specific actions for fields
                Object.keys(data).forEach((field) => {
                  const fieldAction = EduContentDataFieldActionFactory.getLoadedAction(
                    eduContentId,
                    field,
                    data[field],
                    data
                  );
                  if (fieldAction) {
                    actions.push(fieldAction);
                  }
                });
              }
              // Always dispatch Success, though it might be less useful now
              actions.push(new LoadDataSuccess({ eduContentId, data }));
              return from(actions);
            })
          );
        },
        onError: (action: LoadData, error) => {
          return new LoadDataFail({ eduContentId: action.payload.eduContentId, error });
        },
      })
    )
  );
}
